import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "../api.ts";

/** How long a tick stays reversible where it was made. */
export const UNDO_WINDOW_MS = 10_000;

export interface Undoable {
  completionId: number;
  what: string;
  who: string | null;
}

interface UndoApi {
  /** Offer the way back for a tick that was just made. */
  offer: (item: Undoable) => void;
}

const UndoContext = createContext<UndoApi>({ offer: () => {} });

export function useUndo(): UndoApi {
  return useContext(UndoContext);
}

/**
 * The ten seconds after a tap, everywhere.
 *
 * Ticking a job makes it vanish from the list it was in, so a wrong tap needs
 * a way out — but only for the person still standing there, and only for the
 * thing they just did. Once the window closes the entry stands, and removing
 * it becomes a parent's deliberate act in the admin page. The server enforces
 * the same rule, so this is a real window rather than a UI courtesy.
 *
 * One toast at the app root rather than one per screen: a second tick replaces
 * the first, which is also the honest behaviour — you can only take back the
 * last thing you did.
 */
export function UndoProvider({ children, onUndone }: { children: ReactNode; onUndone?: () => void }) {
  const [item, setItem] = useState<Undoable | null>(null);
  const offer = useCallback((next: Undoable) => setItem(next), []);

  return (
    <UndoContext.Provider value={{ offer }}>
      {children}
      {item ? (
        <UndoToast
          item={item}
          onExpire={() => setItem(null)}
          onUndo={(entry) => {
            setItem(null);
            void api.undoCompletion(entry.completionId).then(() => onUndone?.());
          }}
        />
      ) : null}
    </UndoContext.Provider>
  );
}

/**
 * Deliberately quiet: it sits in a corner, states what happened, and counts
 * itself down. A receipt with a way out, not an alert.
 */
export function UndoToast({
  item,
  onUndo,
  onExpire,
}: {
  item: Undoable;
  onUndo: (item: Undoable) => void;
  onExpire: () => void;
}) {
  const [remaining, setRemaining] = useState(UNDO_WINDOW_MS);

  useEffect(() => {
    setRemaining(UNDO_WINDOW_MS);
    const started = Date.now();
    const timer = window.setInterval(() => {
      const left = UNDO_WINDOW_MS - (Date.now() - started);
      if (left <= 0) {
        window.clearInterval(timer);
        onExpire();
      } else {
        setRemaining(left);
      }
    }, 250);
    return () => window.clearInterval(timer);
    // Keyed on the entry: a second tick restarts the window for the new one.
  }, [item.completionId, onExpire]);

  return (
    <div className="toast" role="status">
      <span className="toast__text">
        <strong>{item.what}</strong>
        {item.who ? ` — ${item.who}` : ""}
      </span>
      <button type="button" className="toast__undo" onClick={() => onUndo(item)}>
        Undo <span className="toast__count">{Math.ceil(remaining / 1000)}</span>
      </button>
      <span className="toast__bar" style={{ width: `${(remaining / UNDO_WINDOW_MS) * 100}%` }} aria-hidden="true" />
    </div>
  );
}
